import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import footerLogo from "@/assets/logo.png";
import heroBg from "@/assets/hero-bg.png.asset.json";
import hydrolossLogo from "@/assets/hydroloss-logo.png.asset.json";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { TenantHome } from "@/components/TenantHome";

export default function Home() {
  const { user, loading: authLoading } = useAuth();
  const { currentTenant, tenants, loading: tenantLoading } = useTenant();

  if (user && (authLoading || tenantLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user && (currentTenant || tenants.length > 0)) {
    return <TenantHome />;
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      <Helmet>
        <title>HydroLoss Field Suite — Water Network Survey & Management</title>
        <meta name="description" content="Smart water asset management — surveys, leak detection, NRW reduction and DMA design." />
        <link rel="canonical" href="https://dma-data-capture-gabcle.lovable.app/" />
        <meta property="og:title" content="HydroLoss Field Suite — Water Network Survey & Management" />
        <meta property="og:description" content="Smart water asset management — surveys, leak detection, NRW reduction and DMA design." />
        <meta property="og:url" content="https://dma-data-capture-gabcle.lovable.app/" />
      </Helmet>
      {/* Header */}
      <header className="shrink-0 top-0 z-50 border-b bg-primary backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <span className="text-xl font-bold text-primary-foreground tracking-tight">
            DMA Data Capture Hub
          </span>
          <div className="flex items-center gap-3">
            <Button asChild size="sm" className="gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-2xl">
              <Link to="/login">
                Sign In
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden flex flex-1 min-h-0 items-center bg-cover bg-center bg-no-repeat bg-fixed"
        style={{ backgroundImage: `url(${heroBg.url})` }}
      >
        <div className="container relative py-6 md:py-10">
          <div className="relative max-w-3xl p-4 md:p-6 text-left space-y-4 md:space-y-5">
            <div className="relative space-y-3">
              <span className="inline-flex items-center rounded-full border border-primary-foreground/30 bg-primary-foreground/15 px-3 py-1 text-xs md:text-sm font-medium text-primary-foreground">
                Precision Data for Decisive NRW Action
              </span>
              <div className="flex items-center gap-4">
                <img
                  src={hydrolossLogo.url}
                  alt="HydroLoss Field Suite logo"
                  className="h-12 w-auto object-contain md:h-16"
                  loading="lazy"
                />
                <h1 className="text-balance text-xl font-bold tracking-tight text-primary-foreground sm:text-2xl md:text-3xl" style={{ textShadow: '0 2px 10px hsl(var(--primary) / 0.6)' }}>
                  HydroLoss Field Suite
                </h1>
              </div>
            </div>

            <div className="relative space-y-3 md:space-y-4 text-balance text-primary-foreground/95">
              <p className="text-sm md:text-base leading-snug line-clamp-3 md:line-clamp-none" style={{ textShadow: '0 1px 6px hsl(var(--primary) / 0.5)' }}>
                HydroLoss Field Suite platform delivers intelligent, real‑time control across the entire water network. It transforms traditional DMA operations into a unified, data‑driven ecosystem—combining field surveys, valve status tracking, chamber construction monitoring, leak detection, and NRW intelligence in one streamlined hub.
              </p>
              <p className="text-sm md:text-base leading-snug line-clamp-3 md:line-clamp-none" style={{ textShadow: '0 1px 6px hsl(var(--primary) / 0.5)' }}>
                With high‑accuracy asset capture, isolation zone visibility, and integrated SCADA, flow meter, and pressure logger data, utilities can understand network conditions instantly and act decisively.
              </p>
              <p className="text-sm md:text-base leading-snug line-clamp-3 md:line-clamp-none" style={{ textShadow: '0 1px 6px hsl(var(--primary) / 0.5)' }}>
                Built for performance, reliability, and sustainability, HydroLoss Field Suite enables smarter operational decisions and stronger water infrastructure. Predictive maintenance, automated NRW workflows, and consolidated analytics help reduce losses, optimize distribution, and extend asset life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="shrink-0 border-t bg-primary">
        <div className="container py-2 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary-foreground flex items-center justify-center">
              <img src={footerLogo} alt="Gabcle DevHub" className="h-5 w-5 object-contain" />
            </div>
            <span className="text-sm font-semibold text-primary-foreground">Gabcle DevHub</span>
          </div>
          <p className="text-xs text-primary-foreground/70">
            © {new Date().getFullYear()} Gabcle DevHub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
